import { useEffect, useState, type FormEvent } from 'react';
import type { ApiClient, License, Plan } from '../api';
import { formatDate, promptForReason, renderError } from './shared';

/**
 * Licence keys: the non-payment way onto a paid plan. A new key is shown
 * exactly once — only its hash is stored — so copy it before leaving.
 */
export function LicensesView({ client }: { client: ApiClient }) {
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [issued, setIssued] = useState<{ code: string; plan: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ planKey: 'pro', durationDays: '365', maxRedemptions: '1', expiresAt: '', note: '' });

  useEffect(() => {
    let cancelled = false;
    Promise.all([client.listLicenses(), client.getPlans()])
      .then(([list, planList]) => {
        if (cancelled) return;
        setLicenses(list.licenses);
        setPlans(planList.plans.filter((plan) => !plan.isDefault));
      })
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [client, tick]);

  async function issue(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const created = await client.createLicense({
        planKey: form.planKey,
        durationDays: form.durationDays ? Number(form.durationDays) : undefined,
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : undefined,
        expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59Z`).toISOString() : undefined,
        note: form.note.trim(),
      });
      setIssued({ code: created.code, plan: created.plan });
      setForm((current) => ({ ...current, note: '' }));
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(license: License) {
    const reason = await promptForReason(`revoking licence ${license.codePrefix}…`);
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await client.revokeLicense(license.id, reason);
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Billing</p>
          <h2>Licences</h2>
          <p className="muted">Keys customers redeem in the account panel for a plan, without paying.</p>
        </div>
      </div>

      {error !== null && renderError(error)}

      {issued && (
        <div className="panel issued">
          <h2>New {issued.plan} licence — shown once</h2>
          <div className="issued-code">
            <code>{issued.code}</code>
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(issued.code).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="muted small">Only a hash is stored. If this is lost, revoke it and issue another.</p>
        </div>
      )}

      <form className="panel stack" onSubmit={issue}>
        <h2>Issue a licence</h2>
        <div className="form-grid">
          <label>
            Plan
            <select value={form.planKey} onChange={(event) => setForm({ ...form, planKey: event.target.value })}>
              {plans.map((plan) => (
                <option key={plan.key} value={plan.key}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Length (days, empty = lifetime)
            <input inputMode="numeric" value={form.durationDays} onChange={(event) => setForm({ ...form, durationDays: event.target.value })} />
          </label>
          <label>
            Redemptions allowed
            <input inputMode="numeric" value={form.maxRedemptions} onChange={(event) => setForm({ ...form, maxRedemptions: event.target.value })} />
          </label>
          <label>
            Redeemable until (optional)
            <input type="date" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} />
          </label>
        </div>
        <label>
          Why it exists (kept in the audit log)
          <input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="e.g. Partner bundle, ACME order 1234" />
        </label>
        <div>
          <button className="primary" type="submit" disabled={busy || form.note.trim().length < 3}>
            Issue licence
          </button>
        </div>
      </form>

      <div className="panel">
        {licenses === null ? (
          <p className="muted">Loading…</p>
        ) : licenses.length === 0 ? (
          <p className="muted">No licences yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Key starts</th>
                <th>Plan</th>
                <th>Length</th>
                <th>Used</th>
                <th>Redeemable until</th>
                <th>Note</th>
                <th>Issued</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {licenses.map((license) => (
                <tr key={license.id} className={license.revokedAt ? 'retired' : ''}>
                  <td>
                    <code>{license.codePrefix}…</code>
                  </td>
                  <td>{license.plan}</td>
                  <td>{license.durationDays ? `${license.durationDays} days` : 'lifetime'}</td>
                  <td>
                    {license.redemptionCount} / {license.maxRedemptions}
                  </td>
                  <td>{formatDate(license.expiresAt)}</td>
                  <td className="small">{license.note ?? '—'}</td>
                  <td>{formatDate(license.createdAt)}</td>
                  <td>
                    {license.revokedAt ? (
                      <span className="tag suspended">revoked</span>
                    ) : license.redemptionCount >= license.maxRedemptions ? (
                      <span className="tag">used up</span>
                    ) : (
                      <span className="tag active">valid</span>
                    )}
                  </td>
                  <td>
                    {!license.revokedAt && (
                      <button className="danger" disabled={busy} onClick={() => void revoke(license)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
